import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Calendar, Clock, User, Phone, Mail, 
  MoreVertical, Eye, Edit, Trash2, Check, X, Loader2 
} from 'lucide-react';
import './AdminBookings.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const url = statusFilter !== 'all' 
        ? `${API_URL}/api/admin/bookings?status=${statusFilter}`
        : `${API_URL}/api/admin/bookings`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchBookings();
        setShowModal(false);
        setSelectedBooking(null);
      }
    } catch (error) {
      console.error('Failed to update booking:', error);
    } finally {
      setUpdating(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchBookings();
      }
    } catch (error) {
      console.error('Failed to delete booking:', error);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client_phone?.includes(searchTerm);
    return matchesSearch;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: { bg: '#fef3c7', color: '#d97706', border: '#fcd34d' },
      confirmed: { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
      in_progress: { bg: '#e0e7ff', color: '#4f46e5', border: '#a5b4fc' },
      completed: { bg: '#dcfce7', color: '#16a34a', border: '#86efac' },
      cancelled: { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' }
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-bookings" data-testid="admin-bookings">
      <div className="page-header">
        <div>
          <h1>Bookings</h1>
          <p>Manage all your booking requests</p>
        </div>
        <div className="header-stats">
          <span className="stat-pill pending">{bookings.filter(b => b.status === 'pending').length} Pending</span>
          <span className="stat-pill confirmed">{bookings.filter(b => b.status === 'confirmed').length} Confirmed</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by client name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="booking-search-input"
          />
        </div>
        <div className="filter-group">
          <Filter size={18} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading bookings...</span>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="empty-container">
          <Calendar size={48} />
          <h3>No bookings found</h3>
          <p>When clients book your services, they'll appear here.</p>
        </div>
      ) : (
        <div className="bookings-grid">
          {filteredBookings.map((booking) => {
            const statusStyle = getStatusColor(booking.status);
            return (
              <div key={booking.id} className="booking-card" data-testid={`booking-card-${booking.id}`}>
                <div className="booking-header">
                  <span 
                    className="status-tag" 
                    style={{ 
                      background: statusStyle.bg, 
                      color: statusStyle.color,
                      borderColor: statusStyle.border 
                    }}
                  >
                    {booking.status?.replace('_', ' ')}
                  </span>
                  <div className="booking-actions">
                    <button 
                      className="action-btn view"
                      onClick={() => { setSelectedBooking(booking); setShowModal(true); }}
                      data-testid={`view-booking-${booking.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => deleteBooking(booking.id)}
                      data-testid={`delete-booking-${booking.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="booking-client">
                  <User size={16} />
                  <span className="client-name">{booking.client_name}</span>
                </div>

                <div className="booking-details">
                  <div className="detail-row">
                    <Calendar size={14} />
                    <span>{formatDate(booking.booking_date)}</span>
                  </div>
                  <div className="detail-row">
                    <Phone size={14} />
                    <span>{booking.client_phone}</span>
                  </div>
                  <div className="detail-row">
                    <Mail size={14} />
                    <span className="email-text">{booking.client_email}</span>
                  </div>
                </div>

                <div className="booking-service">
                  <span className="service-label">Service:</span>
                  <span className="service-name">{booking.service_name}</span>
                </div>

                {booking.notes && (
                  <div className="booking-notes">
                    <p>{booking.notes.substring(0, 80)}{booking.notes.length > 80 ? '...' : ''}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Booking Detail Modal */}
      {showModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="booking-detail-modal">
            <div className="modal-header">
              <h2>Booking Details</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Client Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name</label>
                    <span>{selectedBooking.client_name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <span>{selectedBooking.client_email}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone</label>
                    <span>{selectedBooking.client_phone}</span>
                  </div>
                  <div className="info-item">
                    <label>Country</label>
                    <span>{selectedBooking.client_country || 'India'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Booking Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Service</label>
                    <span>{selectedBooking.service_name}</span>
                  </div>
                  <div className="info-item">
                    <label>Booking Date</label>
                    <span>{formatDate(selectedBooking.booking_date)}</span>
                  </div>
                  <div className="info-item">
                    <label>Blocked Until</label>
                    <span>{formatDate(selectedBooking.blocked_until)}</span>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <span 
                      className="status-tag" 
                      style={getStatusColor(selectedBooking.status)}
                    >
                      {selectedBooking.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {selectedBooking.notes && (
                <div className="detail-section">
                  <h3>Project Notes</h3>
                  <p className="notes-text">{selectedBooking.notes}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-buttons">
                  {['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      className={`status-btn ${selectedBooking.status === status ? 'active' : ''}`}
                      style={selectedBooking.status === status ? getStatusColor(status) : {}}
                      onClick={() => updateBookingStatus(selectedBooking.id, status)}
                      disabled={updating}
                      data-testid={`set-status-${status}`}
                    >
                      {updating ? <Loader2 size={14} className="spin" /> : null}
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
